import { gsap } from "gsap";

export default class FiltersHandler {
    constructor() {
        this.currentFilters = {
            post_type: window.filterAjax?.post_type || 'cpt-library',
            taxonomies: {}, // Changed to object to hold multiple taxonomies: { 'tax-library-type': 'slug', 'tax-library-topic': 'slug' }
            search: '',
            paged: 1,
            sort_by: 'menu-order',
            posts_per_page: 9,
            exclude_posts: []
        };
        this.isLoading = false;
        this.ajaxUrl = window.filterAjax?.ajaxurl || '/wp-admin/admin-ajax.php';
        this.nonce = window.filterAjax?.nonce || '';
        this.initialPostType = this.currentFilters.post_type; // Store initial post type
        this._programmaticUpdate = false; // Flag to prevent event loops when updating UI
        this.getFeaturedPosts(); // Get featured post IDs to exclude
    }
    
    getFeaturedPosts() {
        // Get featured post IDs from data attributes on the page
        const postsContainer = document.querySelector('[data-posts-container]');
        if (postsContainer && postsContainer.dataset.excludePosts) {
            try {
                this.currentFilters.exclude_posts = JSON.parse(postsContainer.dataset.excludePosts);
            } catch (e) {
                console.error('Failed to parse exclude_posts:', e);
            }
        }
    }

    init() {
        // Check if we have any filtering elements on this page
        const hasSearch = document.querySelector('[data-search-input]');
        const hasTaxonomies = document.querySelector('[data-taxonomy]');
        const hasPostsContainer = document.querySelector('[data-posts-container]');

        if (!hasSearch && !hasTaxonomies && !hasPostsContainer) {
            return;
        }

        // Check if post type has changed and reset filters if needed
        this.checkPostTypeChange();
        
        // Listen for tab changes from taxonomies (blog, resources)
        const taxonomyContainers = document.querySelectorAll('[data-custom-pagination]');
        
        taxonomyContainers.forEach(container => {
            container.addEventListener('tabChanged', (event) => {
                const { index, tab } = event.detail;
                const containerTarget = container.getAttribute('data-custom-pagination');
                
                // Handle library-taxonomies differently - use direct filtering
                if (containerTarget === 'library-taxonomies') {
                    // Check if this is the All button or a specific taxonomy
                    const showAllAttr = tab.getAttribute('data-show-all');
                    const taxonomyAttr = tab.getAttribute('data-taxonomy');
                    const termSlug = tab.getAttribute('data-term-slug');
                    
                    if (showAllAttr) {
                        // All button clicked - clear library type filters
                        delete this.currentFilters.taxonomies[showAllAttr];
                        this.clearActiveFilters(showAllAttr);
                        this.currentFilters.paged = 1;
                        this.executeFilter();
                    } else if (taxonomyAttr && termSlug) {
                        // Specific library type clicked (News, Blog, etc)
                        this.currentFilters.taxonomies[taxonomyAttr] = termSlug;
                        this.currentFilters.paged = 1;
                        this.executeFilter();
                    }
                } else {
                    // Other targets use the switchTaxonomyGroup method
                    this.switchTaxonomyGroup(index);
                }
            });
        });
        
        // Initialize taxonomy filter buttons
        this.initTaxonomyButtons();
        
        // Initialize search integration
        this.initSearch();
        
        // Initialize clear button
        this.initClearButton();
        
        // Initialize sort dropdown
        this.initSortDropdown();
        
        // Handle browser back/forward
        this.initPopState();
        
        // Initialize from URL on page load
        this.initFromURL();
    }
    
    checkPostTypeChange() {
        const currentPostType = window.filterAjax?.post_type || 'cpt-library';

        // If post type has changed, reset all filters
        if (this.initialPostType !== currentPostType) {
            this.resetFilters();
            this.initialPostType = currentPostType;
            this.currentFilters.post_type = currentPostType;
        }
    }

    resetFilters() {
        // Clear all filter states
        this.currentFilters = {
            post_type: window.filterAjax?.post_type || 'cpt-library',
            taxonomies: {},
            search: '',
            paged: 1,
            sort_by: 'menu-order',
            posts_per_page: 9,
            exclude_posts: []
        };

        // Re-get featured posts after reset
        this.getFeaturedPosts();
        
        // Clear active filter buttons
        this.clearActiveFilters();
        
        // Clear search input
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput) {
            searchInput.value = '';
        }
        
        // Clear URL parameters (but don't trigger history change)
        if (window.location.search) {
            const cleanURL = window.location.pathname;
            window.history.replaceState({ filters: this.currentFilters }, '', cleanURL);
        }
    }
    
    switchTaxonomyGroup(tabIndex) {
        const taxonomyGroups = document.querySelectorAll('[data-taxonomy-group]');
        
        // Hide all groups first
        taxonomyGroups.forEach(group => {
            gsap.to(group, { 
                opacity: 0, 
                duration: 0.2,
                onComplete: () => {
                    group.style.display = 'none';
                }
            });
        });
        
        // Show the selected group
        setTimeout(() => {
            const targetTaxonomy = tabIndex === 0 ? 'tax-news-type' : 'tax-news-topic';
            const targetGroup = document.querySelector(`[data-taxonomy-group="${targetTaxonomy}"]`);
            
            if (targetGroup) {
                targetGroup.style.display = 'flex';
                gsap.fromTo(targetGroup, 
                    { opacity: 0 }, 
                    { opacity: 1, duration: 0.3 }
                );
                
                // Clear any active filters when switching
                this.clearActiveFilters();
            }
        }, 200);
    }
    
    initTaxonomyButtons() {
        // Handle "Show all" buttons
        const showAllButtons = document.querySelectorAll('[data-show-all]');

        showAllButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const taxonomy = e.target.getAttribute('data-show-all');

                // Clear this specific taxonomy filter
                delete this.currentFilters.taxonomies[taxonomy];
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        });
        
        // Handle button filters
        const taxonomyButtons = document.querySelectorAll('[data-taxonomy][data-term-id]');
        
        taxonomyButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const taxonomy = e.target.getAttribute('data-taxonomy');
                const termSlug = e.target.getAttribute('data-term-slug');
                
                // Toggle active state
                const isActive = e.target.classList.contains('active');
                
                if (isActive) {
                    this.removeFilter(e.target);
                } else {
                    this.addFilter(e.target, taxonomy, termSlug);
                }
            });
        });
        
        // Handle select dropdown filters
        const taxonomySelects = document.querySelectorAll('[data-taxonomy-select]');

        taxonomySelects.forEach(select => {
            select.addEventListener('change', (e) => {
                // Skip if this is a programmatic update from updateUIFromFilters()
                if (this._programmaticUpdate) return;
                const taxonomy = e.target.getAttribute('data-taxonomy-select');
                const termSlug = e.target.value;

                // Clear any active button filters for this taxonomy
                this.clearActiveFilters(taxonomy);

                if (termSlug) {
                    // Add/update this taxonomy filter
                    this.currentFilters.taxonomies[taxonomy] = termSlug;
                } else {
                    // Remove this taxonomy filter
                    delete this.currentFilters.taxonomies[taxonomy];
                }

                // Reset to page 1 and execute filter
                this.currentFilters.paged = 1;
                this.executeFilter();
            });
        });
    }
    
    initSearch() {
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput) {
            let searchTimeout;
            
            // Handle search input
            searchInput.addEventListener('input', (e) => {
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(() => {
                    this.currentFilters.search = e.target.value;
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }, 500);
            });
            
            // Handle Enter key
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    clearTimeout(searchTimeout);
                    this.currentFilters.search = e.target.value;
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }
            });
        }
        
        // Handle clear button
        const clearButton = document.querySelector('[data-search-clear]');
        if (clearButton) {
            clearButton.addEventListener('click', () => {
                const searchInput = document.querySelector('[data-search-input]');
                if (searchInput) {
                    searchInput.value = '';
                    this.currentFilters.search = '';
                    this.currentFilters.paged = 1;
                    this.executeFilter();
                }
            });
        }
    }
    
    initClearButton() {
        const clearButton = document.querySelector('[data-clear-filters]');
        if (clearButton) {
            clearButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearAllFilters();
            });
        }
    }
    
    clearAllFilters() {
        // Reset filter state
        this.currentFilters.taxonomies = {};
        this.currentFilters.search = '';
        this.currentFilters.paged = 1;
        this.currentFilters.sort_by = 'menu-order';
        
        // Clear UI elements
        this.clearActiveFilters();
        
        // Clear search input
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput) {
            searchInput.value = '';
        }
        
        // Reset sort dropdown
        const sortSelect = document.querySelector('[data-sort-select]');
        const sortLabel = document.querySelector('[data-sort-label]');
        
        if (sortSelect) {
            sortSelect.value = 'menu-order';
        } else if (sortLabel) {
            sortLabel.textContent = 'Sort by';
        }
        
        // Execute filter to show all posts
        this.executeFilter();
    }
    
    initSortDropdown() {
        // Handle new select-based sort dropdown
        const sortSelect = document.querySelector('[data-sort-select]');
        
        if (sortSelect) {
            sortSelect.addEventListener('change', (e) => {
                this.currentFilters.sort_by = e.target.value;
                this.currentFilters.paged = 1; // Reset to first page
                this.executeFilter();
            });
            return;
        }
        
        // Fallback to old dropdown method
        const sortTrigger = document.querySelector('[data-sort-trigger]');
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        const sortLabel = document.querySelector('[data-sort-label]');
        const sortOptions = document.querySelectorAll('[data-sort-option]');
        
        if (!sortTrigger || !sortMenu) return;
        
        // Toggle dropdown
        sortTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = !sortMenu.classList.contains('hidden');
            
            if (isOpen) {
                this.closeSortDropdown();
            } else {
                this.openSortDropdown();
            }
        });
        
        // Handle sort option clicks
        sortOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                e.preventDefault();
                const sortValue = option.getAttribute('data-sort-option');
                const sortText = option.textContent.trim();
                
                // Update filter and UI
                this.currentFilters.sort_by = sortValue;
                this.currentFilters.paged = 1; // Reset to first page
                
                // Update button text
                if (sortLabel) {
                    sortLabel.textContent = sortText;
                }
                
                // Close dropdown
                this.closeSortDropdown();
                
                // Execute filter
                this.executeFilter();
            });
        });
        
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            const sortDropdown = document.querySelector('[data-sort-dropdown]');
            if (sortDropdown && !sortDropdown.contains(e.target)) {
                this.closeSortDropdown();
            }
        });
    }
    
    openSortDropdown() {
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        
        if (sortMenu) {
            sortMenu.classList.remove('hidden');
        }
        if (sortIcon) {
            sortIcon.style.transform = 'rotate(180deg)';
        }
    }
    
    closeSortDropdown() {
        const sortMenu = document.querySelector('[data-sort-menu]');
        const sortIcon = document.querySelector('[data-sort-icon]');
        
        if (sortMenu) {
            sortMenu.classList.add('hidden');
        }
        if (sortIcon) {
            sortIcon.style.transform = 'rotate(0deg)';
        }
    }
    
    initPopState() {
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.filters) {
                this.currentFilters = { ...e.state.filters };
                this.executeFilter(false); // Don't update URL
                this.updateUIFromFilters();
            }
        });
    }
    
    initFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const search = urlParams.get('search');
        const paged = parseInt(urlParams.get('paged')) || 1;

        // Check for taxonomy-specific query parameters
        const typeSlug = urlParams.get('type');
        const topicSlug = urlParams.get('topic');
        const locationSlug = urlParams.get('location');

        // Map query params to taxonomies based on current post type
        if (typeSlug) {
            // Determine taxonomy based on post type context
            if (this.currentFilters.post_type === 'cpt-library') {
                this.currentFilters.taxonomies['tax-library-type'] = typeSlug;
            } else if (this.currentFilters.post_type === 'cpt-events') {
                this.currentFilters.taxonomies['tax-event-type'] = typeSlug;
            }
        }
        if (topicSlug && this.currentFilters.post_type === 'cpt-library') {
            // Topic taxonomy for library
            this.currentFilters.taxonomies['tax-library-topic'] = topicSlug;
        }
        if (locationSlug && this.currentFilters.post_type === 'cpt-events') {
            // Location taxonomy for events
            this.currentFilters.taxonomies['tax-location'] = locationSlug;
        }

        if (search) {
            this.currentFilters.search = search;
        }
        this.currentFilters.paged = paged;

        if (Object.keys(this.currentFilters.taxonomies).length > 0 || search || paged > 1) {
            this.executeFilter(false); // Don't update URL on init
            this.updateUIFromFilters();
        }
    }
    
    addFilter(button, taxonomy, termSlug) {
        // Add/update this taxonomy filter
        this.currentFilters.taxonomies[taxonomy] = termSlug;
        this.currentFilters.paged = 1; // Reset to first page
        this.executeFilter();
    }

    removeFilter(button) {
        const taxonomy = button.getAttribute('data-taxonomy');

        // Remove this taxonomy filter
        if (taxonomy) {
            delete this.currentFilters.taxonomies[taxonomy];
        }
        this.currentFilters.paged = 1;
        this.executeFilter();
    }
    
    clearActiveFilters(specificTaxonomy = null) {
        // Clear button filters
        const activeButtons = specificTaxonomy 
            ? document.querySelectorAll(`[data-taxonomy="${specificTaxonomy}"].active`)
            : document.querySelectorAll('[data-taxonomy].active');
            
        // activeButtons.forEach(button => {
        //     button.classList.remove('active');
        // });
        
        // Clear select dropdowns
        const taxonomySelects = specificTaxonomy
            ? document.querySelectorAll(`[data-taxonomy-select="${specificTaxonomy}"]`)
            : document.querySelectorAll('[data-taxonomy-select]');
            
        taxonomySelects.forEach(select => {
            select.value = '';
        });
    }
    
    updateUIFromFilters() {
        // Update active filter buttons for each taxonomy
        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            const activeButton = document.querySelector(
                `[data-taxonomy="${taxonomy}"][data-term-slug="${termSlug}"]`
            );
            // Optional: Add active class if needed
            // if (activeButton) {
            //     activeButton.classList.add('active');
            // }

            // Update select dropdowns
            const select = document.querySelector(`[data-taxonomy-select="${taxonomy}"]`);
            if (select) {
                // Set flag to prevent change event from firing executeFilter()
                this._programmaticUpdate = true;
                select.value = termSlug;
                // Reset flag immediately after setting value
                this._programmaticUpdate = false;
            }
        });

        // Update search input
        const searchInput = document.querySelector('[data-search-input]');
        if (searchInput && this.currentFilters.search) {
            searchInput.value = this.currentFilters.search;
        }
    }
    
    executeFilter(updateURL = true) {
        if (this.isLoading) return;
        
        this.isLoading = true;
        this.showLoadingState();
        
        const formData = new FormData();
        formData.append('action', 'filter_posts');
        formData.append('nonce', this.nonce);
        formData.append('post_type', this.currentFilters.post_type);
        formData.append('search', this.currentFilters.search);
        formData.append('paged', this.currentFilters.paged);
        formData.append('sort_by', this.currentFilters.sort_by);
        formData.append('posts_per_page', this.currentFilters.posts_per_page);

        // Add multiple taxonomies as JSON
        formData.append('taxonomies', JSON.stringify(this.currentFilters.taxonomies));

        // Add excluded post IDs (featured posts)
        if (this.currentFilters.exclude_posts && this.currentFilters.exclude_posts.length > 0) {
            this.currentFilters.exclude_posts.forEach(id => {
                formData.append('exclude_posts[]', id);
            });
        }
        
        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.updateResults(data.data);
                    this.updateUIFromFilters();
                if (updateURL) {
                    this.updateURL();
                }
            } else {
                console.error('Filter request failed:', data);
            }
        })
        .catch(error => {
            console.error('AJAX error:', error);
        })
        .finally(() => {
            this.isLoading = false;
            this.hideLoadingState();
        });
    }
    
    showLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            container.style.opacity = '0.5';
            container.style.pointerEvents = 'none';
        }
    }
    
    hideLoadingState() {
        const container = document.querySelector('[data-posts-container]');
        if (container) {
            gsap.to(container, { opacity: 1, duration: 0.3 });
            container.style.pointerEvents = 'auto';
        }
    }
    
    updateResults(data) {
        const container = document.querySelector('[data-posts-container]');
        if (!container) return;

        // Fade out current content
        gsap.to(container, {
            opacity: 0,
            duration: 0.2,
            onComplete: () => {
                // Replace content
                container.innerHTML = data.posts.join('');

                // Update results count
                const resultsNumber = document.querySelector('[data-results-number]');
                if (resultsNumber && data.pagination) {
                    resultsNumber.textContent = data.pagination.total_posts;
                }

                // Update pagination if exists
                this.updatePagination(data.pagination);
                // Update UI elements including dropdown display
                this.updateUIFromFilters();

                // Fade in new content
                gsap.fromTo(container,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.3 }
                );
            }
        });
    }
    
    updatePagination(pagination) {
        const paginationContainer = document.querySelector('[data-pagination-container]');

        if (!paginationContainer) return;

        // If only one page or no pages, hide pagination
        if (!pagination || pagination.total_pages <= 1) {
            paginationContainer.innerHTML = '';
            return;
        }

        const currentPage = pagination.current_page;
        const totalPages = pagination.total_pages;
        const showDots = totalPages > 5;

        let html = '';

        // Previous button
        if (currentPage > 1) {
            html += `<a href="#" data-page="${currentPage - 1}" class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer flex items-center justify-center pagination-link transition hover:font-bold">‹</a>`;
        }

        // Calculate range
        const start = Math.max(1, currentPage - 2);
        const end = Math.min(totalPages, currentPage + 2);

        // First page + dots
        if (showDots && start > 1) {
            html += `<a href="#" data-page="1" class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer flex items-center justify-center pagination-link transition hover:font-bold${currentPage === 1 ? ' font-bold' : ''}">1</a>`;
            if (start > 2) {
                html += `<span class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer font-black flex items-center justify-center">...</span>`;
            }
        }

        // Page numbers
        for (let i = start; i <= end; i++) {
            html += `<a href="#" data-page="${i}" class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer flex items-center justify-center pagination-link transition hover:font-bold${currentPage === i ? ' font-bold' : ''}">${i}</a>`;
        }

        // Dots + last page
        if (showDots && end < totalPages) {
            if (end < totalPages - 1) {
                html += `<span class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer font-black flex items-center justify-center">...</span>`;
            }
            html += `<a href="#" data-page="${totalPages}" class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer flex items-center justify-center pagination-link transition hover:font-bold${currentPage === totalPages ? ' font-bold' : ''}">${totalPages}</a>`;
        }

        // Next button
        if (currentPage < totalPages) {
            html += `<a href="#" data-page="${currentPage + 1}" class="w-20 sm:w-10 h-20 sm:h-10 text-center rounded-xs cursor-pointer flex items-center justify-center pagination-link transition hover:font-bold">›</a>`;
        }

        paginationContainer.innerHTML = html;

        // Add click handlers to new pagination links
        this.initPaginationHandlers();
    }

    initPaginationHandlers() {
        const paginationLinks = document.querySelectorAll('.pagination-link');

        paginationLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const page = parseInt(e.target.getAttribute('data-page'));

                if (page && page !== this.currentFilters.paged) {
                    this.currentFilters.paged = page;
                    this.executeFilter();

                    // Scroll to top of posts container
                    const container = document.querySelector('[data-posts-container]');
                    if (container) {
                        const offset = 100; // Offset for header
                        const elementPosition = container.getBoundingClientRect().top + window.pageYOffset;
                        window.scrollTo({
                            top: elementPosition - offset,
                            behavior: 'smooth'
                        });
                    }
                }
            });
        });
    }
    
    updateURL() {
        const params = new URLSearchParams();

        // Add all taxonomy filters to URL
        Object.entries(this.currentFilters.taxonomies).forEach(([taxonomy, termSlug]) => {
            // All "type" taxonomies use ?type=
            if (taxonomy.includes('-type')) {
                params.set('type', termSlug);
            }
            // All "topic" taxonomies use ?topic=
            else if (taxonomy.includes('-topic')) {
                params.set('topic', termSlug);
            }
            // Location taxonomy uses ?location=
            else if (taxonomy === 'tax-location') {
                params.set('location', termSlug);
            }
        });

        if (this.currentFilters.search) {
            params.set('search', this.currentFilters.search);
        }
        if (this.currentFilters.paged > 1) {
            params.set('paged', this.currentFilters.paged);
        }

        const newURL = window.location.pathname + (params.toString() ? '?' + params.toString() : '');

        // Push state for browser history
        window.history.pushState(
            { filters: this.currentFilters },
            '',
            newURL
        );
    }
}